/**
 * @fileoverview Export reply of the Sales Performance mock endpoints.
 * @description Answers POST /sales/performance/export for a Super Admin with the individual summary
 * and the target detail as one raw text/csv attachment (UTF-8 BOM, formula-looking cells quoted
 * safely), and refuses anyone else with 403. It does not model the fresh-sign-in (`403/2009`) or
 * size (`409/4132`) refusals of the real backend.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { mockCaller } from './sales-crm-mock-projection'
import { bodyOf, failReply } from './sales-crm-mock-response.utils'
import { percentOf, roleCodeOf, scopeOf, targetsFor } from './sales-performance-mock-model'
import { isReply, rankedPeople, selectionOf } from './sales-performance-mock-selection'

import type { AxiosRequestConfig } from 'axios'

/**
 * @description Quotes one CSV cell, prefixing a formula-looking value with an apostrophe.
 * @param {string | number} value - The cell value.
 * @returns {string} The quoted cell.
 */
function cell(value: string | number): string {
  const text = String(value)
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text
  return `"${safe.replaceAll('"', '""')}"`
}

/**
 * @description Answers Export Sales Performance: the individual summary and the target detail as
 * one CSV attachment, for a Super Admin only.
 * @param {AxiosRequestConfig} config - The request config, for the JSON body.
 * @returns {[number, string | object, Record<string, string>?]} The reply.
 */
export function performanceExportReply(
  config: AxiosRequestConfig,
): [number, string | object, Record<string, string>?] {
  const caller = mockCaller()
  if (!caller || !scopeOf(caller).canExport) return failReply(403, '2008', 'Not allowed')
  const selection = selectionOf(bodyOf(config))
  if (isReply(selection)) return selection
  const { period, people, today } = selection
  const targets = targetsFor(new Set(people.map((user) => user.id)), period, true)
  const lines = [
    [cell('Sales Performance'), cell(period.label)].join(','),
    [cell('Exported by'), cell(caller.name), cell(today)].join(','),
    '',
    cell('INDIVIDUAL SUMMARY'),
    ['Rank', 'Name', 'Role', 'Assigned', 'Achieved', 'Achievement %'].map(cell).join(','),
    ...rankedPeople(selection).map(({ user, assigned, achieved }, index) =>
      [
        index + 1,
        user.name,
        roleCodeOf(user),
        assigned,
        achieved,
        percentOf(achieved, assigned) ?? '',
      ]
        .map(cell)
        .join(','),
    ),
    '',
    cell('TARGET DETAIL'),
    ['Bank', 'Owner', 'Due date', 'Achieved'].map(cell).join(','),
    ...targets.map((item) =>
      [
        item.tenant.name,
        people.find((user) => user.id === item.target.ownerId)?.name ?? '',
        item.target.endDate,
        item.achieved ? 'Yes' : 'No',
      ]
        .map(cell)
        .join(','),
    ),
  ]
  return [
    200,
    `\uFEFF${lines.join('\r\n')}\r\n`,
    {
      'content-type': 'text/csv; charset=utf-8',
      'content-disposition': `attachment; filename="sales-performance-${period.code.toLowerCase()}-${today}.csv"`,
    },
  ]
}
